import { useEffect, useState } from "react";
import { Loader, ActionIcon, Tooltip, Text, Paper, Group } from "@mantine/core";
import { DataTable, ConfirmModal, Button, Alert, Badge } from "@xplatform/design-system";
import type { DataTableColumn } from "@xplatform/design-system";
import {
    IconPlus,
    IconEdit,
    IconTrash,
    IconCategory,
} from "@tabler/icons-react";
import { useTranslation } from "@hooks/useTranslation";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { useCategory } from "@hooks/useCategory";
import { CategoryDialog } from "@src/features";
import type { CategoryViewModel } from "@models/category";
import { getLastPartOfUid } from "@src/utils/uidUtil";
import "./CategoryManagementPage.scss";

export const CategoryManagementPage = () => {
    const { t } = useTranslation();
    const { isCrossOrg } = useAdminPermissions();
    const { categories, isLoading, error, fetchCategories, deleteCategory } =
        useCategory();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editingCategory, setEditingCategory] =
        useState<CategoryViewModel | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingCategory, setDeletingCategory] =
        useState<CategoryViewModel | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleOpenCreateDialog = () => {
        setCreateDialogOpen(true);
    };

    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
    };

    const handleEditCategory = (category: CategoryViewModel) => {
        setEditingCategory(category);
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingCategory(null);
    };

    const handleDeleteClick = (category: CategoryViewModel) => {
        setDeletingCategory(category);
        setDeleteDialogOpen(true);
        setDeleteError(null);
    };

    const handleConfirmDelete = async () => {
        if (!deletingCategory) return;
        try {
            await deleteCategory(deletingCategory.id);
            setDeleteDialogOpen(false);
            setDeletingCategory(null);
        } catch (err) {
            setDeleteError(
                err instanceof Error
                    ? err.message
                    : t("admin.categories.messages.deleteFailed"),
            );
        }
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setDeletingCategory(null);
        setDeleteError(null);
    };

    const handleSuccess = async () => {
        await fetchCategories();
    };

    const columns: DataTableColumn<CategoryViewModel>[] = [
        {
            key: "id",
            header: "ID",
            width: "10%",
            render: (category) => (
                <span className="category-page__cell-id">
                    {getLastPartOfUid(category.id)}
                </span>
            ),
        },
        {
            key: "code",
            header: t("admin.table.columns.code"),
            width: "12%",
            render: (category) => (
                <Badge
                    variant="outline"
                    size="sm"
                    className="category-page__code-chip"
                >
                    {category.code}
                </Badge>
            ),
        },
        {
            key: "name",
            header: t("admin.table.columns.name"),
            width: "14%",
            render: (category) => (
                <Text size="sm" fw={500}>
                    {category.name}
                </Text>
            ),
        },
        {
            key: "description",
            header: t("admin.table.columns.description"),
            width: "26%",
            render: (category) => (
                <Text
                    size="sm"
                    c="dimmed"
                    className="category-page__cell-description"
                >
                    {category.description || "-"}
                </Text>
            ),
        },
        {
            key: "created",
            header: t("admin.table.columns.created"),
            width: "14%",
            render: (category) => (
                <Text size="sm" c="dimmed">
                    {category.createdAt?.toLocaleDateString() || "-"}
                </Text>
            ),
        },
        {
            key: "updated",
            header: t("admin.table.columns.updated"),
            width: "14%",
            render: (category) => (
                <Text size="sm" c="dimmed">
                    {category.updatedAt?.toLocaleDateString() || "-"}
                </Text>
            ),
        },
        {
            key: "actions",
            header: t("admin.table.columns.actions"),
            width: "10%",
            align: "right",
            render: (category) => (
                <Group gap={4} justify="flex-end">
                    <Tooltip label={t("common.edit")}>
                        <ActionIcon
                            variant="subtle"
                            color="blue"
                            size="sm"
                            onClick={() => handleEditCategory(category)}
                        >
                            <IconEdit size={16} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t("common.delete")}>
                        <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            onClick={() => handleDeleteClick(category)}
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            ),
        },
    ];

    return (
        <div className="admin-page-content">
            <Group justify="space-between" align="center" mb="lg">
                <Text component="h1" size="lg" fw={600}>
                    {t("admin.categories.title")}
                </Text>
                {isCrossOrg && (
                    <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={handleOpenCreateDialog}
                    >
                        {t("admin.categories.actions.createCategory")}
                    </Button>
                )}
            </Group>

            {error && (
                <Alert color="red" mb="sm">
                    {error}
                </Alert>
            )}

            {isLoading ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "2rem 0",
                    }}
                >
                    <Loader />
                </div>
            ) : categories.length === 0 ? (
                <div className="category-page__empty-state">
                    <IconCategory size={48} color="gray" />
                    <Text size="lg" c="dimmed" mb="xs">
                        {t("admin.categories.empty.noCategories")}
                    </Text>
                    <Text size="sm" c="dimmed">
                        {t("admin.categories.empty.description")}
                    </Text>
                </div>
            ) : (
                <Paper className="category-page__table-container">
                    <DataTable<CategoryViewModel>
                        className="category-page__table"
                        fz="sm"
                        verticalSpacing="xs"
                        horizontalSpacing="md"
                        columns={columns}
                        data={categories}
                        getRowKey={(category) => category.id}
                    />
                </Paper>
            )}

            <CategoryDialog
                open={createDialogOpen}
                onClose={handleCloseCreateDialog}
                onSuccess={handleSuccess}
            />

            {editingCategory && (
                <CategoryDialog
                    open={editDialogOpen}
                    onClose={handleCloseEditDialog}
                    onSuccess={handleSuccess}
                    category={editingCategory}
                />
            )}

            <ConfirmModal
                opened={deleteDialogOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title={t("common.delete")}
                confirmLabel={t("common.delete")}
                cancelLabel={t("common.cancel")}
                danger
                size="xs"
                portalProps={{ target: "#admin-portal-root" }}
                message={
                    <>
                        <Text size="sm">
                            {t("admin.categories.messages.deleteConfirm")}
                        </Text>
                        {deleteError && (
                            <Alert color="red" mt="sm">
                                {deleteError}
                            </Alert>
                        )}
                    </>
                }
            />
        </div>
    );
};
